/**
 * Documents page — table-first (Stripe-like).
 * Columns: Document Type, Required?, Status, Expiration, Last Updated, Actions.
 * Filters: Required, Missing, Expiring soon, Uploaded. Detail drawer on row click.
 */
import React, { useState, useEffect, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useTour } from '../contexts/TourContext';
import ProductTour from './Tour/ProductTour';
import { documentsTour } from '../tours/tourConfigs';
import DocumentUpload from './Documents/DocumentUpload';
import VisionVerificationBadge from './Documents/VisionVerificationBadge';
import { API_URL } from '../config';
import { hasPendingVisionVerification } from '../utils/documentVision';
import { PageHeader, DataTable, Drawer, Badge, Button, EmptyState } from './ui';
import { BrandLoading } from './Brand';
import { FileText, Download, Trash2, Upload } from 'lucide-react';
import axios from 'axios';
import '../App.css';
import { DOC_TYPE_LABELS, DOC_TYPE_REQUIRED, FMCSA_REF } from '../config/documentTypes';

const Documents = () => {
  const [searchParams] = useSearchParams();
  const uploadTaggedEntityType = searchParams.get('taggedEntityType') || '';
  const uploadTaggedEntityId = searchParams.get('taggedEntityId') || '';
  const uploadDocumentType = searchParams.get('documentType') || '';

  const { isTourComplete, markTourComplete } = useTour();
  const [showTour, setShowTour] = useState(false);
  const [documents, setDocuments] = useState([]);
  const [vehicles, setVehicles] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');
  const [filter, setFilter] = useState('all'); // all | required | missing | expiring | uploaded
  const [sortBy, setSortBy] = useState('expirationDate');
  const [deletingId, setDeletingId] = useState(null);
  const [drawerDoc, setDrawerDoc] = useState(null);
  const [showUpload, setShowUpload] = useState(false);

  useEffect(() => {
    if (!isTourComplete('documents')) setTimeout(() => setShowTour(true), 500);
  }, [isTourComplete]);
  const handleTourFinish = () => {
    setShowTour(false);
    markTourComplete('documents');
  };

  useEffect(() => {
    fetchDocuments();
    fetchVehicles();
  }, []);

  const pendingVisionVerification = useMemo(
    () => hasPendingVisionVerification(documents),
    [documents],
  );

  useEffect(() => {
    if (!pendingVisionVerification) return undefined;
    const intervalId = setInterval(() => {
      fetchDocuments();
    }, 5000);
    return () => clearInterval(intervalId);
  }, [pendingVisionVerification]);

  useEffect(() => {
    if (uploadTaggedEntityType && uploadTaggedEntityId) {
      setShowUpload(true);
    }
  }, [uploadTaggedEntityType, uploadTaggedEntityId]);

  const fetchDocuments = async () => {
    try {
      setIsLoading(true);
      setError('');
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_URL}/api/documents`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setDocuments(response.data || []);
    } catch (err) {
      console.error('Error fetching documents:', err);
      setError('Failed to load documents');
    } finally {
      setIsLoading(false);
    }
  };

  const fetchVehicles = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_URL}/api/vehicles`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setVehicles(response.data || []);
    } catch (err) {
      console.error('Error fetching vehicles:', err);
    }
  };

  const getDocumentStatus = (doc) => {
    if (!doc.expirationDate) {
      return { status: 'valid', label: 'Valid', variant: 'success' };
    }
    const expiration = new Date(doc.expirationDate);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    expiration.setHours(0, 0, 0, 0);
    const days = Math.ceil((expiration - today) / (1000 * 60 * 60 * 24));
    if (days < 0) return { status: 'expired', label: 'Expired', variant: 'danger' };
    if (days <= 7) return { status: 'urgent', label: `Expires in ${days}d`, variant: 'warning' };
    if (days <= 30) return { status: 'expiring', label: `Expires in ${days}d`, variant: 'warning' };
    return { status: 'valid', label: 'Valid', variant: 'success' };
  };

  const filteredAndSorted = useMemo(() => {
    let list = [...documents];
    if (filter === 'required') {
      list = list.filter((d) => DOC_TYPE_REQUIRED[d.documentType] === true);
    } else if (filter === 'expiring') {
      list = list.filter((d) => {
        const s = getDocumentStatus(d);
        return s.status === 'expired' || s.status === 'urgent' || s.status === 'expiring';
      });
    } else if (filter === 'uploaded') {
      list = list.filter((d) => d.uploadedAt);
    }
    list.sort((a, b) => {
      if (sortBy === 'uploadedAt') return new Date(b.uploadedAt || 0) - new Date(a.uploadedAt || 0);
      if (sortBy === 'expirationDate') {
        if (!a.expirationDate && !b.expirationDate) return 0;
        if (!a.expirationDate) return 1;
        if (!b.expirationDate) return -1;
        return new Date(a.expirationDate) - new Date(b.expirationDate);
      }
      return 0;
    });
    return list;
  }, [documents, filter, sortBy]);

  const handleDelete = async (documentId) => {
    if (!window.confirm('Are you sure you want to delete this document?')) return;
    try {
      setDeletingId(documentId);
      const token = localStorage.getItem('token');
      await axios.delete(`${API_URL}/api/documents/${documentId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      await fetchDocuments();
      if (drawerDoc?.id === documentId) setDrawerDoc(null);
    } catch (err) {
      console.error('Error deleting document:', err);
      alert('Failed to delete document. Please try again.');
    } finally {
      setDeletingId(null);
    }
  };

  const handleDownload = async (documentId) => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_URL}/api/documents/${documentId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const url = response.data?.downloadUrl || response.data?.fileUrl;
      if (url) {
        const fullUrl = url.startsWith('http') ? url : `${API_URL}${url}`;
        window.open(fullUrl, '_blank');
      }
    } catch (err) {
      console.error('Error downloading document:', err);
      alert('Failed to download document. Please try again.');
    }
  };

  const columns = [
    {
      key: 'documentType',
      label: 'Document Type',
      render: (val, row) => (
        <span className="documents-type-cell">
          <span>{DOC_TYPE_LABELS[val] || val}</span>
          <VisionVerificationBadge doc={row} compact />
        </span>
      ),
    },
    { key: 'required', label: 'Required?', render: (_, row) => (DOC_TYPE_REQUIRED[row.documentType] ? 'Yes' : 'No') },
    { key: 'status', label: 'Status', render: (_, row) => <Badge variant={getDocumentStatus(row).variant}>{getDocumentStatus(row).label}</Badge> },
    { key: 'expirationDate', label: 'Expiration', render: (val) => (val ? new Date(val).toLocaleDateString() : '—') },
    { key: 'uploadedAt', label: 'Last Updated', render: (val) => (val ? new Date(val).toLocaleDateString() : '—') },
    {
      key: 'actions',
      label: 'Actions',
      render: (_, row) => (
        <span className="doc-actions">
          <button type="button" className="ui-btn ui-btn--ghost ui-btn--sm" onClick={(e) => { e.stopPropagation(); handleDownload(row.id); }} aria-label="Download">
            <Download size={16} />
          </button>
          <button type="button" className="ui-btn ui-btn--ghost ui-btn--sm" onClick={(e) => { e.stopPropagation(); handleDelete(row.id); }} disabled={deletingId === row.id} aria-label="Delete">
            <Trash2 size={16} />
          </button>
        </span>
      ),
    },
  ];

  if (isLoading) {
    return (
      <div className="page-container">
        <div className="dashboard-loading">
          <BrandLoading message="Loading documents..." />
        </div>
      </div>
    );
  }

  return (
    <div className="page-container documents-page">
      <ProductTour steps={documentsTour} run={showTour} onFinish={handleTourFinish} tourName="documents" />
      <PageHeader
        title="Documents"
        subtitle="Manage compliance documents. Click a row for details."
        primaryAction={{ label: 'Upload document', icon: Upload, onClick: () => setShowUpload(true) }}
        secondaryAction={{ label: 'Refresh', onClick: fetchDocuments }}
      />
      {error && <div className="dashboard-error"><p>{error}</p></div>}

      {showUpload && (
        <DocumentUpload
          onUploadSuccess={() => {
            fetchDocuments();
            setShowUpload(false);
          }}
          vehicles={vehicles}
          onClose={() => setShowUpload(false)}
          initialTaggedEntityType={uploadTaggedEntityType}
          initialTaggedEntityId={uploadTaggedEntityId}
          initialDocumentType={uploadDocumentType}
        />
      )}

      <div className="documents-toolbar">
        <div className="documents-filters">
          <span className="documents-filter-label">Filter:</span>
          {['all', 'required', 'expiring', 'uploaded'].map((f) => (
            <button
              key={f}
              type="button"
              className={`ui-tabs__tab ${filter === f ? 'ui-tabs__tab--active' : ''}`}
              onClick={() => setFilter(f)}
            >
              {f === 'all' ? 'All' : f === 'required' ? 'Required' : f === 'expiring' ? 'Expiring soon' : 'Uploaded'}
            </button>
          ))}
        </div>
        <select className="topbar__selector" value={sortBy} onChange={(e) => setSortBy(e.target.value)}>
          <option value="expirationDate">Sort: Expiration</option>
          <option value="uploadedAt">Sort: Last Updated</option>
        </select>
      </div>

      {filteredAndSorted.length === 0 ? (
        <EmptyState
          icon={FileText}
          title="No documents"
          description="Upload your first document to track medical cards, insurance, registrations, and more."
          actionLabel="Upload document"
          onAction={() => setShowUpload(true)}
        />
      ) : (
        <DataTable
          columns={columns}
          rows={filteredAndSorted}
          onRowClick={(row) => setDrawerDoc(row)}
          emptyMessage="No documents match the filter."
        />
      )}

      <Drawer open={!!drawerDoc} onClose={() => setDrawerDoc(null)} title={drawerDoc ? DOC_TYPE_LABELS[drawerDoc.documentType] || 'Document' : ''}>
        {drawerDoc && (
          <>
            <p className="text-body text-muted">{drawerDoc.notes || 'No description.'}</p>
            <p className="text-small"><strong>FMCSA reference:</strong> {FMCSA_REF[drawerDoc.documentType] || '—'}</p>
            <p className="text-small"><strong>Last uploaded:</strong> {drawerDoc.uploadedAt ? new Date(drawerDoc.uploadedAt).toLocaleString() : '—'}</p>
            <p className="text-small"><strong>Expiration:</strong> {drawerDoc.expirationDate ? new Date(drawerDoc.expirationDate).toLocaleDateString() : '—'}</p>
            <div className="documents-drawer-vision">
              <VisionVerificationBadge doc={drawerDoc} />
            </div>
            <div style={{ marginTop: 16, display: 'flex', gap: 8 }}>
              <Button variant="primary" icon={Download} onClick={() => handleDownload(drawerDoc.id)}>Download</Button>
              <Button variant="destructive" icon={Trash2} onClick={() => handleDelete(drawerDoc.id)} disabled={deletingId === drawerDoc.id}>Delete</Button>
            </div>
          </>
        )}
      </Drawer>
    </div>
  );
};

export default Documents;
